/**
 * DeepLinkModal (FEAT-DEEPLINK-001) — create / edit a single library entry in a
 * popup (house convention: modals, not drawers). Links stay editable; the backend
 * enforces scheme safety + uniqueness.
 */
import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Textarea, Select, Button,
  VStack, HStack, Text, useToast,
} from '@chakra-ui/react';
import deepLinksApi from '../../api/deepLinks';

const empty = { name: '', category: '', description: '', android_link: '', ios_link: '', web_fallback_url: '', status_id: 1 };

export default function DeepLinkModal({ isOpen, onClose, initial, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial && initial.id);

  useEffect(() => {
    if (isOpen) setForm(initial ? { ...empty, ...initial } : empty);
  }, [isOpen, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const clientValid = () => {
    if (!form.name.trim()) { toast({ title: 'Name is required', status: 'warning' }); return false; }
    if (!form.android_link.trim() && !form.ios_link.trim()) {
      toast({ title: 'Add at least one of Android / iOS link', status: 'warning' }); return false;
    }
    return true;
  };

  const save = async () => {
    if (!clientValid()) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim(),
        android_link: form.android_link.trim(),
        ios_link: form.ios_link.trim(),
        web_fallback_url: form.web_fallback_url.trim(),
        status_id: Number(form.status_id),
      };
      if (isEdit) {
        await deepLinksApi.update({ id: initial.id, ...body });
        toast({ title: 'Deep link updated', status: 'success' });
      } else {
        await deepLinksApi.create(body);
        toast({ title: 'Deep link created', status: 'success' });
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.error || e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px" fontWeight={700}>{isEdit ? 'Edit deep link' : 'New deep link'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4} pt={1}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Name</FormLabel>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Product detail" />
            </FormControl>
            <HStack align="start" spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm">Category</FormLabel>
                <Input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Catalog" />
              </FormControl>
              <FormControl maxW="40%">
                <FormLabel fontSize="sm">Status</FormLabel>
                <Select value={form.status_id} onChange={(e) => set('status_id', e.target.value)}>
                  <option value={1}>Active</option>
                  <option value={0}>Archived</option>
                </Select>
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} placeholder="Optional" />
            </FormControl>

            <Text fontSize="xs" color="gray.500" pt={1}>
              At least one of Android / iOS is required. Use <code>https://</code> or a custom <code>scheme://</code> (e.g. <code>myapp://product/123</code>).
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Android deep link</FormLabel>
              <Input value={form.android_link} onChange={(e) => set('android_link', e.target.value)} placeholder="myapp://product/123" fontFamily="mono" fontSize="13px" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">iOS deep link</FormLabel>
              <Input value={form.ios_link} onChange={(e) => set('ios_link', e.target.value)} placeholder="myapp://product/123" fontFamily="mono" fontSize="13px" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Web fallback URL</FormLabel>
              <Input value={form.web_fallback_url} onChange={(e) => set('web_fallback_url', e.target.value)} placeholder="https://shop.example.com/p/123" fontFamily="mono" fontSize="13px" />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button colorScheme="brand" onClick={save} isLoading={saving}>{isEdit ? 'Save changes' : 'Create'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
